import React from 'react';

import styled from '@emotion/styled';

import { FontWeight, LARGE_TITLE_STYLES, Text } from '@clutter/clean';

import {
  useGeocodeQuery,
  useOrderCancellationPolicyGroupQuery,
} from '@graphql/platform';

import { useStorageCheckoutContext } from '@root/components/checkout/context';
import { WT_VISITOR_TOKEN } from '@root/initializers/wt';

import { Spacer } from '@shared/spacer';

import { PolicyModal } from './subcomponents/policy_modal';

const Body = styled(Text.Body)`
  padding-top: 16px;
  padding-bottom: 40px;
`;

export const SmartStorageBillingModal: React.FC<{
  isOpen: boolean;
  handleModalClose(): void;
}> = ({ isOpen, handleModalClose }) => {
  const {
    pricingSummary,
    flowState: { values },
  } = useStorageCheckoutContext();
  const geocode =
    useGeocodeQuery({
      variables: { zip: values.zip! },
      skip: !values.zip,
    }).data?.geocode ?? undefined;
  const { data } = useOrderCancellationPolicyGroupQuery({
    variables: { visitorToken: WT_VISITOR_TOKEN },
    skip: !geocode || geocode?.region?.name === 'Los Angeles',
  });
  if (!pricingSummary) return null;

  const cancellationPolicy =
    data?.orderCancellationPolicyGroup?.onboardingCancellationPolicy;
  return (
    <PolicyModal isOpen={isOpen} handleModalClose={() => handleModalClose()}>
      <Text style={LARGE_TITLE_STYLES.SM}>When will I be charged?</Text>
      <Body>
        Don’t worry, making a reservation is completely free! Your first month
        of storage and pickup fee will be charged after your appointment is
        completed.
        <Spacer height="12px" />
        We will perform a pre-authorization check with the card on file 7 days
        before your appointment to verify sufficient funds are available to
        cover the appointment. If you are booking within 7 days of your
        appointment then we will perform the check immediately after booking.
        The check will be immediately released once the pre-authorization check
        is completed.
      </Body>
      <Text.Headline weight={FontWeight.Medium}>
        What if I need to reschedule or cancel?
      </Text.Headline>
      <Body>
        {cancellationPolicy ? (
          cancellationPolicy.paragraph
        ) : (
          <>
            We ask that you reschedule or cancel at least 48hrs before your
            appointment. Otherwise, you will be charged a $100 charge. This
            charge is increased to $200 on the same day of the appointment.
          </>
        )}
        You can reschedule or cancel online in just a few clicks.
      </Body>
    </PolicyModal>
  );
};
